import { ReactNode, useEffect, useRef, useState } from 'react';
import { tv } from 'tailwind-variants';

export interface ClarionCollapseProps {
  children?: ReactNode;
  component?: React.ElementType;
  isOpen?: boolean;
  keepChildrenMounted?: boolean;
  transitionDuration?: number;
  className?: string;
}

const collapseStyles = tv({
  base: 'transition-all ease-in-out',
  variants: {
    isOpen: {
      true: '',
      false: 'h-0',
    },
  },
});

export function ClarionCollapse({
  children,
  component: Component = 'div',
  isOpen = false,
  keepChildrenMounted = false,
  transitionDuration = 200,
  className,
  ...props
}: ClarionCollapseProps) {
  const [height, setHeight] = useState<string | number>(isOpen ? 'auto' : 0);
  const [isAnimating, setIsAnimating] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (isOpen) {
      // Opening
      setIsAnimating(true);

      // Measure the content height
      if (contentRef.current) {
        const scrollHeight = contentRef.current.scrollHeight;
        setHeight(scrollHeight);

        timeoutRef.current = setTimeout(() => {
          setHeight('auto');
          setIsAnimating(false);
        }, transitionDuration);
      }
    } else {
      // Closing
      if (contentRef.current) {
        const scrollHeight = contentRef.current.scrollHeight;
        setHeight(scrollHeight);
        setIsAnimating(true);

        // Force a reflow to ensure the height is set before transitioning to 0
        requestAnimationFrame(() => {
          setHeight(0);

          timeoutRef.current = setTimeout(() => {
            setIsAnimating(false);
          }, transitionDuration);
        });
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isOpen, transitionDuration]);

  const shouldRenderChildren = isOpen || keepChildrenMounted || isAnimating;

  return (
    <Component
      {...props}
      className={collapseStyles({ isOpen: isOpen || isAnimating, className })}
      style={{
        height: height === 'auto' ? undefined : height,
        transitionDuration: `${transitionDuration}ms`,
        overflow: height === 'auto' ? 'visible' : 'hidden',
      }}
    >
      <div ref={contentRef}>{shouldRenderChildren && children}</div>
    </Component>
  );
}
